import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AdminAuthProvider, useAdminAuth } from '@/hooks/useAdminAuth';
import { useAcessoAdminUpavet } from '@/hooks/useAcessoAdminUpavet';
import { rotuloAcao, rotuloRecurso } from '@/utils/adminPermissoes';

interface RotaProtegidaProps {
  /** Recurso exigido, ex.: UPAVET_CASTROMETRO. */
  recurso: string;
  /**
   * Recurso alternativo aceito no lugar do principal.
   * Usado em telas que unificaram dois assuntos (ex.: procedimentos + catálogo):
   * quem tem qualquer uma das permissões pode entrar.
   */
  recursoAlternativo?: string;
  /**
   * Lista de recursos alternativos aceitos (telas que reúnem mais de um assunto).
   * Basta o operador ter UM deles, além do recurso principal.
   */
  recursosAlternativos?: string[];
  /** Ação exigida (padrão: visualizar). */
  acao?: string;
  /**
   * Exige também a liberação do e-mail na allowlist do servidor
   * (views administrativas /upavet/admin/*).
   */
  allowlist?: boolean;
  children: ReactNode;
}

/** Página exibida quando o operador não tem a permissão exigida. */
function SemPermissao({ recurso, acao }: { recurso: string; acao: string }) {
  return (
    <div className="flex min-h-[70vh] items-center justify-center bg-background-100 px-4 py-10">
      <div className="w-full max-w-lg rounded-2xl border border-background-200/70 bg-background-50 p-8 text-center">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-accent-100 text-accent-700">
          <i className="ri-lock-2-line text-2xl" />
        </span>
        <h1 className="mt-4 font-heading text-lg font-bold text-foreground-950">Sem permissão</h1>
        <p className="mt-2 text-sm text-foreground-600">
          Sua conta não possui a permissão{' '}
          <strong className="text-foreground-900">
            {rotuloRecurso(recurso)}: {rotuloAcao(acao)}
          </strong>{' '}
          necessária para abrir esta página.
        </p>
        <div className="mt-4 rounded-xl bg-background-100 px-4 py-3 text-left">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500">
            Recurso exigido
          </p>
          <p className="mt-1 font-mono text-xs text-foreground-800">
            {recurso}:{acao}
          </p>
        </div>
        <p className="mt-3 text-[11px] leading-relaxed text-foreground-500">
          O acesso é decidido no servidor. Se precisar desta permissão, solicite ao administrador
          responsável pelos perfis.
        </p>
        <Link
          to="/admin/perfil"
          className="mt-5 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
        >
          <i className="ri-user-settings-line" />
          Ver meu perfil
        </Link>
      </div>
    </div>
  );
}

/** Aviso da allowlist do servidor (permissão existe, e-mail ainda não liberado). */
function AllowlistPendente() {
  return (
    <div className="flex min-h-[70vh] items-center justify-center bg-background-100 px-4 py-10">
      <div className="w-full max-w-lg rounded-2xl border border-background-200/70 bg-background-50 p-8 text-center">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-secondary-100 text-secondary-800">
          <i className="ri-shield-keyhole-line text-2xl" />
        </span>
        <h1 className="mt-4 font-heading text-lg font-bold text-foreground-950">
          Acesso pendente de liberação
        </h1>
        <p className="mt-2 text-sm text-foreground-600">
          Acesso aos dados de cadastro pendente de liberação no servidor (allowlist).
        </p>
        <p className="mt-3 text-[11px] leading-relaxed text-foreground-500">
          Seu perfil já possui a permissão necessária. As views administrativas só são liberadas
          depois que o e-mail da conta entra na allowlist do servidor.
        </p>
        <Link
          to="/upavet/atendimentos"
          className="mt-5 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-200 px-5 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100 cursor-pointer"
        >
          <i className="ri-arrow-left-line" />
          Voltar para a UPA VET
        </Link>
      </div>
    </div>
  );
}

/** Guarda interna: só decide depois de o acesso estar resolvido. */
function Guarda({ recurso, recursoAlternativo, recursosAlternativos, acao, allowlist, children }: RotaProtegidaProps) {
  const { status, pode } = useAdminAuth();
  const acessoAdmin = useAcessoAdminUpavet();

  if (status === 'carregando') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100">
        <div className="flex flex-col items-center gap-3 text-foreground-600">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-600" />
          <p className="text-sm">Verificando permissões…</p>
        </div>
      </div>
    );
  }

  // Sessão/estado são tratados pelo AdminShell da própria página
  // (login, senha provisória, MFA, conta sem vínculo).
  if (status !== 'autorizado') return <>{children}</>;

  const liberado =
    pode(recurso, acao) ||
    (recursoAlternativo ? pode(recursoAlternativo, acao) : false) ||
    (recursosAlternativos ? recursosAlternativos.some((r) => pode(r, acao)) : false);

  if (!liberado) {
    return <SemPermissao recurso={recurso} acao={acao} />;
  }

  if (allowlist && acessoAdmin === 'negado') {
    return <AllowlistPendente />;
  }

  return <>{children}</>;
}

/**
 * Envolve uma rota administrativa exigindo RECURSO:AÇÃO.
 *
 * A permissão vem do servidor (permissões efetivas = papel + exceções). A guarda
 * não substitui a checagem do backend: ela apenas evita abrir a tela sem acesso.
 *
 * O `AdminAuthProvider` é idempotente: quando a página monta o AdminShell, o
 * provedor existente é reutilizado e nenhuma consulta extra é feita.
 */
export default function RotaProtegida({
  recurso,
  recursoAlternativo,
  recursosAlternativos,
  acao = 'visualizar',
  allowlist,
  children,
}: RotaProtegidaProps) {
  return (
    <AdminAuthProvider>
      <Guarda
        recurso={recurso}
        recursoAlternativo={recursoAlternativo}
        recursosAlternativos={recursosAlternativos}
        acao={acao}
        allowlist={allowlist}
      >
        {children}
      </Guarda>
    </AdminAuthProvider>
  );
}